import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const REACTIONS = [
  { key: 'heart', emoji: '❤️', label: 'Love it' },
  { key: 'thumbs_up', emoji: '👍', label: 'Like' },
  { key: 'fire', emoji: '🔥', label: 'Fire' },
  { key: 'clap', emoji: '👏', label: 'Clap' },
  { key: 'bulb', emoji: '💡', label: 'Insightful' },
  { key: 'party', emoji: '🎉', label: 'Celebrate' },
];

const storageKey = (slug) => `blog_reactions_${slug}`;

const readMyReactions = (slug) => {
  try {
    const raw = localStorage.getItem(storageKey(slug));
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
};

const writeMyReactions = (slug, data) => {
  try {
    localStorage.setItem(storageKey(slug), JSON.stringify(data));
  } catch {
    // ignore storage errors
  }
};

const ReactionsBar = ({ slug }) => {
  const [counts, setCounts] = useState({});
  const [myReactions, setMyReactions] = useState({});
  const [pending, setPending] = useState({});

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const res = await axios.get(`${API}/blogs/${slug}/reactions`);
        if (!cancelled) setCounts(res.data || {});
      } catch {
        if (!cancelled) setCounts({});
      }
    };
    if (slug) {
      setMyReactions(readMyReactions(slug));
      load();
    }
    return () => { cancelled = true; };
  }, [slug]);

  const totalReactions = Object.values(counts).reduce((sum, v) => sum + (v || 0), 0);

  const handleReact = async (key) => {
    if (pending[key]) return;
    const isActive = !!myReactions[key];
    const action = isActive ? 'remove' : 'add';

    // Snapshot for potential rollback
    const prevCounts = counts;
    const prevMine = myReactions;

    // Optimistic update
    setPending((p) => ({ ...p, [key]: true }));
    const nextCounts = { ...counts, [key]: Math.max(0, (counts[key] || 0) + (action === 'add' ? 1 : -1)) };
    const nextMine = { ...myReactions, [key]: !isActive };
    if (!nextMine[key]) delete nextMine[key];
    setCounts(nextCounts);
    setMyReactions(nextMine);
    writeMyReactions(slug, nextMine);

    try {
      const res = await axios.post(`${API}/blogs/${slug}/reactions`, { emoji: key, action });
      setCounts(res.data || nextCounts);
    } catch (err) {
      // Rollback on failure
      setCounts(prevCounts);
      setMyReactions(prevMine);
      writeMyReactions(slug, prevMine);
      toast.error('Could not update reaction. Please try again.');
    } finally {
      setPending((p) => {
        const copy = { ...p };
        delete copy[key];
        return copy;
      });
    }
  };

  return (
    <div className="my-12 bg-white border-2 border-slate-100 rounded-3xl p-6 md:p-8" data-testid="reactions-bar">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
        <div>
          <h3 className="text-2xl font-bold text-slate-900">How did this article make you feel?</h3>
          <p className="text-slate-600 text-sm">Tap any emoji to react — no signup needed.</p>
        </div>
        <div className="text-sm font-semibold text-teal-600" data-testid="reactions-total">
          {totalReactions} {totalReactions === 1 ? 'reaction' : 'reactions'}
        </div>
      </div>

      <div className="grid grid-cols-3 sm:grid-cols-6 gap-3">
        {REACTIONS.map((r) => {
          const active = !!myReactions[r.key];
          const count = counts[r.key] || 0;
          return (
            <button
              key={r.key}
              type="button"
              onClick={() => handleReact(r.key)}
              disabled={!!pending[r.key]}
              className={`group flex flex-col items-center justify-center gap-1 py-4 rounded-2xl border-2 transition-all duration-200 ${
                active
                  ? 'border-teal-500 bg-teal-50 shadow-md'
                  : 'border-slate-100 hover:border-teal-200 hover:bg-slate-50'
              } ${pending[r.key] ? 'opacity-60 cursor-wait' : 'cursor-pointer'}`}
              data-testid={`reaction-btn-${r.key}`}
              aria-label={r.label}
              title={r.label}
            >
              <span className={`text-3xl transition-transform ${active ? 'scale-110' : 'group-hover:scale-110'}`}>
                {r.emoji}
              </span>
              <span className={`text-sm font-bold ${active ? 'text-teal-700' : 'text-slate-700'}`} data-testid={`reaction-count-${r.key}`}>
                {count}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default ReactionsBar;
